import { hasSeenCookieNotice } from '@/utils/consent';
import { getObref, getOpprefForSignup } from '@/utils/openai-ads-attribution';
import { stripSensitiveQuery } from '@/utils/strip-sensitive-query';

/**
 * The OpenAI Ads click reference of this visit, in the flat shape the API stores in `openai_ads_click_ref`
 * (SCRUM-2398). Merged into the four signup bodies: `/user`, `/user/trial`, both SSO logins and
 * `/user/trial/start`.
 */
export interface OpenAiAdsClickRefBody {
  oppref?: string;
  obref?: string;
  consent?: boolean;
  source_url?: string;
}

/**
 * The fragment to merge into a signup body, empty when the visitor did not come from an ad — so an
 * organic signup posts exactly what it posted before. Absent references are omitted, never `null` or `''`.
 *
 * `sourceUrl` exists for the Discord callback, whose own URL is `/auth/discord/callback`: it passes the
 * page that started the OAuth round-trip instead. Spread it BEFORE the caller's own fields, like `clickIdBody()`.
 * The oppref comes from this visit only (URL, then sessionStorage), never from the long-lived `__oppref` cookie.
 * Never throws: attribution must not cost a signup.
 */
export function openAiAdsClickRefBody(sourceUrl?: string): OpenAiAdsClickRefBody {
  if (typeof window === 'undefined') return {};

  try {
    const oppref = getOpprefForSignup();
    const obref = getObref();
    if (!oppref && !obref) return {};

    return {
      ...(oppref ? { oppref } : {}),
      ...(obref ? { obref } : {}),
      // Since 20/10 this means "saw the cookie notice", not consent (SCRUM-1954): `false` is a notice not yet seen, never a refusal
      // (there is no way to refuse), and the send does not filter on `consent = 1`. Its meaning is open in SCRUM-2385.
      consent: hasSeenCookieNotice(),
      source_url: stripSensitiveQuery(sourceUrl ?? window.location.href),
    };
  } catch {
    return {};
  }
}
